import { BeneficiaryType, Currency } from '@seapoint/server/enums';
import { ComponentProps, ReactNode, useState } from 'react';
import { Button } from '../../../../design-system/button/button';
import { Inline } from '../../../../design-system/inline/inline';
import { Popover } from '../../../../design-system/popover/popover';
import { Stack } from '../../../../design-system/stack/stack';
import { SupportedCountryCurrency } from '../../../../types/supported-country-currency';
import { exhaustiveCheck } from '../../../../utils/exhaustive-check';
import { BeneficiaryReviewCard } from '../invoice-data-section/review-matches-tab/beneficiary-review-card';
import { BeneficiaryReviewSection } from '../invoice-data-section/review-matches-tab/beneficiary-review-section';
import { TabValue } from '../invoices-section-tabs/invoices-section-tabs';
import { BeneficiaryCardVariant, InvoiceDrawerBeneficiaryCard } from './invoice-drawer-beneficiary-card';
import { InvoiceDrawerLastPaidCard } from './invoice-drawer-last-paid-card';
import { InvoiceDrawerSummary } from './invoice-drawer-summary';

export type InvoiceDrawerContentBodyVariant = Extract<TabValue, 'TO_PAY' | 'REVIEW_MATCHES'> | 'PAID_NO_TRANSACTION';

export type BeneficiaryCardConfig =
  | {
      isVisible: false;
    }
  | {
      isVisible: true;
      variantConfig: Extract<BeneficiaryCardVariant, 'NEW_BENEFICIARY'>;
      name: string;
    }
  | {
      isVisible: true;
      variantConfig: Extract<BeneficiaryCardVariant, 'SUGGESTED_BENEFICIARY'>;
      beneficiaryType: BeneficiaryType;
      name: string;
      currency: SupportedCountryCurrency;
      iban?: string;
      accountNumber?: string;
      sortCode?: string;
    };

export type LastPaidCardConfig =
  | {
      isVisible: false;
    }
  | {
      isVisible: true;
      lastPaidDate: Date;
      amount: number;
      currency: Currency;
    };

export type SummaryConfig =
  | {
      isVisible: false;
      onMarkAsPaid: () => void;
    }
  | {
      isVisible: true;
      summary: ComponentProps<typeof InvoiceDrawerSummary>['summary'];
      fromEmail: ComponentProps<typeof InvoiceDrawerSummary>['fromEmail'];
      onMarkAsPaid: ComponentProps<typeof InvoiceDrawerSummary>['onMarkAsPaid'];
    };

export type ReadOnlySummaryConfig =
  | {
      isVisible: false;
    }
  | {
      isVisible: true;
      summary: string | undefined;
      fromEmail: string | undefined;
    };

type InvoiceDrawerContentBodyProps = {
  contentBodyConfig:
    | {
        variant: Extract<InvoiceDrawerContentBodyVariant, 'TO_PAY'>;
        isDismissingInvoice: boolean;
        lastPaidCardConfig: LastPaidCardConfig;
        beneficiaryCardConfig: BeneficiaryCardConfig;
        summaryConfig: SummaryConfig;
        onProceedToPayment: () => void;
        onExcludeSender: () => void;
      }
    | {
        variant: Extract<InvoiceDrawerContentBodyVariant, 'REVIEW_MATCHES'>;
        confidenceScore?: number;
        hasMatchedTransaction: boolean;
        cardConfig: ComponentProps<typeof BeneficiaryReviewCard>;
        isApproveMatchLoading: boolean;
        onApproveMatch: () => Promise<void>;
      }
    | {
        variant: Extract<InvoiceDrawerContentBodyVariant, 'PAID_NO_TRANSACTION'>;
        beneficiaryCardConfig: BeneficiaryCardConfig;
        summaryConfig: ReadOnlySummaryConfig;
      };
  onMarkNotInvoice: () => void;
};

export const InvoiceDrawerContentBody = ({ contentBodyConfig, onMarkNotInvoice }: InvoiceDrawerContentBodyProps) => {
  const [isDismissMenuOpen, setIsDismissMenuOpen] = useState(false);
  const { variant } = contentBodyConfig;

  const toggleDismissMenu = () => {
    setIsDismissMenuOpen(!isDismissMenuOpen);
  };

  switch (variant) {
    case 'TO_PAY': {
      const {
        isDismissingInvoice,
        beneficiaryCardConfig,
        lastPaidCardConfig,
        summaryConfig,
        onProceedToPayment,
        onExcludeSender,
      } = contentBodyConfig;

      return (
        <Stack space={3} align="stretch">
          {beneficiaryCardConfig.isVisible && (
            <InvoiceDrawerBeneficiaryCard
              variantConfig={
                beneficiaryCardConfig.variantConfig === 'NEW_BENEFICIARY'
                  ? { variant: beneficiaryCardConfig.variantConfig }
                  : {
                      variant: beneficiaryCardConfig.variantConfig,
                      beneficiaryType: beneficiaryCardConfig.beneficiaryType,
                      currency: beneficiaryCardConfig.currency,
                      iban: beneficiaryCardConfig.iban,
                      accountNumber: beneficiaryCardConfig.accountNumber,
                      sortCode: beneficiaryCardConfig.sortCode,
                    }
              }
              name={beneficiaryCardConfig.name}
            />
          )}

          {lastPaidCardConfig.isVisible && (
            <InvoiceDrawerLastPaidCard
              lastPaidDate={lastPaidCardConfig.lastPaidDate}
              amount={lastPaidCardConfig.amount}
              currency={lastPaidCardConfig.currency}
            />
          )}

          {summaryConfig.isVisible ? (
            <InvoiceDrawerSummary
              summary={summaryConfig.summary}
              fromEmail={summaryConfig.fromEmail}
              onMarkAsPaid={summaryConfig.onMarkAsPaid}
            />
          ) : (
            <Inline>
              <Button variant="link" className="p-0 text-link" onClick={summaryConfig.onMarkAsPaid}>
                Already paid this invoice?
              </Button>
            </Inline>
          )}

          <Stack space={3} align="stretch">
            <Button variant="default" disabled={isDismissingInvoice} onClick={onProceedToPayment}>
              Proceed to Payment
            </Button>

            <Popover
              triggerElement={
                <Button
                  variant="link"
                  onClick={toggleDismissMenu}
                  className="text-destructive"
                  isLoading={isDismissingInvoice}
                >
                  Dismiss Invoice
                </Button>
              }
              content={
                <Stack align="stretch" className="w-full bg-white">
                  <PopoverMenuItemWrapper
                    onClick={() => {
                      onMarkNotInvoice();
                      toggleDismissMenu();
                    }}
                  >
                    <p className="text-sm leading-6 font-normal tracking-tight text-black">This is not an invoice</p>
                  </PopoverMenuItemWrapper>

                  <PopoverMenuItemWrapper
                    onClick={() => {
                      onExcludeSender();
                      toggleDismissMenu();
                    }}
                  >
                    <p className="text-sm leading-6 font-normal tracking-tight text-black">
                      Don't pull in invoices from this sender
                    </p>
                  </PopoverMenuItemWrapper>
                </Stack>
              }
              side="top"
              className="w-full"
            />
          </Stack>
        </Stack>
      );
    }
    case 'REVIEW_MATCHES': {
      const { confidenceScore, hasMatchedTransaction, cardConfig, isApproveMatchLoading, onApproveMatch } =
        contentBodyConfig;

      const reviewSectionConfig = hasMatchedTransaction
        ? {
            variant: 'MATCHED' as const,
            confidenceScore,
            cardConfig,
            isApproveMatchLoading,
            onApproveMatch,
          }
        : {
            variant: 'NOT_MATCHED' as const,
            onChangeMatchedTransaction: cardConfig.onChangeMatchedTransaction,
          };

      return <BeneficiaryReviewSection reviewSectionConfig={reviewSectionConfig} />;
    }
    case 'PAID_NO_TRANSACTION': {
      const { beneficiaryCardConfig, summaryConfig } = contentBodyConfig;

      return (
        <Stack space={3} align="stretch">
          {beneficiaryCardConfig.isVisible && (
            <InvoiceDrawerBeneficiaryCard
              variantConfig={
                beneficiaryCardConfig.variantConfig === 'NEW_BENEFICIARY'
                  ? { variant: beneficiaryCardConfig.variantConfig }
                  : {
                      variant: beneficiaryCardConfig.variantConfig,
                      beneficiaryType: beneficiaryCardConfig.beneficiaryType,
                      currency: beneficiaryCardConfig.currency,
                      iban: beneficiaryCardConfig.iban,
                      accountNumber: beneficiaryCardConfig.accountNumber,
                      sortCode: beneficiaryCardConfig.sortCode,
                    }
              }
              name={beneficiaryCardConfig.name}
            />
          )}

          {summaryConfig.isVisible && (summaryConfig.summary || summaryConfig.fromEmail) ? (
            <Stack space={3} className="rounded-lg border-[0.5px] border-border bg-white p-4">
              {summaryConfig.summary && (
                <Stack space={1}>
                  <h3 className="text-sm leading-6 font-medium tracking-tight text-black">Invoice Summary</h3>

                  <p className="text-xs leading-6 font-normal tracking-tight text-pretty text-muted-foreground">
                    {summaryConfig.summary}
                  </p>
                </Stack>
              )}

              {summaryConfig.fromEmail && (
                <Inline justify="between" wrap="nowrap" className="w-full">
                  <p className="text-sm leading-6 font-medium tracking-tight text-muted-foreground">Received from</p>

                  <p className="w-1/2 overflow-clip text-right text-sm leading-6 font-medium tracking-tight text-ellipsis text-black">
                    {summaryConfig.fromEmail}
                  </p>
                </Inline>
              )}

              <p className="text-sm leading-6 font-semibold tracking-tight text-link">Invoice paid</p>
            </Stack>
          ) : (
            <Inline>
              <p className="text-sm leading-6 font-semibold tracking-tight text-link">Invoice paid</p>
            </Inline>
          )}
        </Stack>
      );
    }
    default:
      return exhaustiveCheck(variant);
  }
};

type PopoverMenuItemWrapperProps = {
  onClick: () => void;
  children: ReactNode;
};

const PopoverMenuItemWrapper = ({ children, onClick }: PopoverMenuItemWrapperProps) => {
  return (
    <Button variant="ghost" onClick={onClick} className="w-full p-0">
      <div className="w-full px-5 py-2 text-left">{children}</div>
    </Button>
  );
};
